import { Component } from '@theme/component';

class HexoCookiePreferences extends Component {
  #controller;
  #busy = false;
  #timer;
  #nativeObserver;
  #previewCopy;
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('click', this.#open, { signal: this.#controller.signal });
    const previewCopy = this.querySelector('[data-hx-cookie-preview-copy]');
    try { this.#previewCopy = previewCopy ? JSON.parse(previewCopy.textContent) : null; } catch { this.#previewCopy = null; }
    this.#nativeObserver?.disconnect();
    this.#nativeObserver = new MutationObserver(this.#localizeNative);
    this.#nativeObserver.observe(document.body, { childList: true });
    this.#localizeNative();
    clearTimeout(this.#timer);
    let attempts = 0;
    const check = () => {
      this.hidden = !window.privacyBanner?.showPreferences;
      if (this.hidden && this.isConnected && attempts++ < 40) this.#timer = setTimeout(check, 250);
    };
    check();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); clearTimeout(this.#timer); this.#nativeObserver?.disconnect(); }
  #localizeNative = () => {
    const close = document.getElementById('shopify-pc__prefs__header-close');
    if (close && this.dataset.close) close.setAttribute('aria-label', this.dataset.close);
    const copy = this.#previewCopy;
    if (!copy || document.documentElement.lang !== 'en') return;
    const replaceText = (selector, key) => {
      document.querySelectorAll(selector).forEach(element => {
        const nodes = [...element.childNodes].filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
        if (!nodes.some(node => /[\u0590-\u05ff]/u.test(node.textContent))) return;
        nodes.forEach((node, index) => { node.textContent = index === 0 ? copy[key] + ' ' : ''; });
      });
    };
    const labels = {
      '#shopify-pc__prefs__header-title': 'preferences_title',
      '#shopify-pc__prefs__header-accept': 'preferences_button_accept_text',
      '#shopify-pc__prefs__header-decline': 'preferences_button_decline_text',
      '#shopify-pc__prefs__header-save': 'preferences_button_save_text',
      '.shopify-pc__prefs__intro-main h3': 'preferences_intro_title',
      '.shopify-pc__prefs__intro-main p': 'preferences_intro_text',
      '#shopify-pc__banner .shopify-pc__banner__body h2': 'title',
      '#shopify-pc__banner .shopify-pc__banner__body p': 'text',
      '#shopify-pc__banner .shopify-pc__banner__body a': 'policy_link_text',
      '#shopify-pc__banner__btn-accept': 'button_accept_text',
      '#shopify-pc__banner__btn-decline': 'button_decline_text',
      '#shopify-pc__banner__btn-manage-prefs': 'button_prefs_open_text',
    };
    for (const [selector, key] of Object.entries(labels)) replaceText(selector, key);
    for (const [id, key] of Object.entries({ essential: 'essential', preferences: 'preferences', marketing: 'marketing', analytics: 'performance' })) {
      replaceText(`#shopify-pc__prefs__${id} label`, `preferences_purposes_${key}_name`);
      replaceText(`#shopify-pc__prefs__${id} > p`, `preferences_purposes_${key}_desc`);
    }
    document.querySelectorAll('#shopify-pc__prefs__dialog, #shopify-pc__banner').forEach(element => {
      element.dir = 'ltr';
      element.lang = 'en';
    });
    document.querySelectorAll('#shopify-pc__banner .shopify-pc__banner__body a').forEach(link => {
      if (link.origin === location.origin && /\/policies\/privacy-policy\/?$/.test(link.pathname)) link.href = copy.policy_link_url;
    });
  };
  #open = async event => {
    const trigger = event.target.closest('[data-hx-cookie-preferences]');
    if (!trigger || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    if (this.#busy) return;
    this.#busy = true;
    trigger.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-hx-cookie-status]');
    if (status) status.textContent = '';
    try {
      if (!window.privacyBanner?.showPreferences) throw new Error('Privacy banner unavailable');
      await window.privacyBanner.showPreferences({ locale: document.documentElement.lang });
      this.#localizeNative();
    } catch {
      if (status && this.isConnected) status.textContent = this.dataset.error;
    } finally {
      trigger.removeAttribute('aria-busy');
      this.#busy = false;
    }
  };
}
if (!customElements.get('hexo-cookie-preferences')) customElements.define('hexo-cookie-preferences', HexoCookiePreferences);
